import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChartLineUp, Coins, Users, ChartBar, Money, Key, FloppyDisk, Receipt, Percent } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { api, brl, errMsg } from "@/lib/api";
import AdminPlansSection from "@/components/AdminPlansSection";
import AdminChatSection from "@/components/AdminChatSection";

export default function Admin() {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState({ access_token: "", public_key: "", mode: "live" });
  const [preview, setPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [wMode, setWMode] = useState("auto");
  const [taxReport, setTaxReport] = useState(null);
  const [allowDelete, setAllowDelete] = useState(false);
  const [fees, setFees] = useState({ bank_fee_rate: 0.03, sales_commission_rate: 0.08, default_affiliate_rate: 0.10 });
  const [feesInput, setFeesInput] = useState({ bank_fee_rate: "3", sales_commission_rate: "8", default_affiliate_rate: "10" });
  const [savingFees, setSavingFees] = useState(false);

  useEffect(() => {
    (async () => {
      const [{ data: r }, { data: s }, w, tr, ds, fs] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/settings"),
        api.get("/admin/settings/withdraw-mode").catch(() => ({ data: { mode: "auto" } })),
        api.get("/admin/tax-report").catch(() => ({ data: null })),
        api.get("/admin/settings/delete-tx").catch(() => ({ data: { enabled: false } })),
        api.get("/admin/settings/fees").catch(() => ({ data: null })),
      ]);
      setData(r);
      setPreview(s.access_token_preview || "");
      setSettings({ access_token: "", public_key: s.public_key || "", mode: s.mode || "live" });
      setWMode(w.data?.mode || "auto");
      setTaxReport(tr.data);
      setAllowDelete(!!ds.data?.enabled);
      if (fs.data) {
        setFees(fs.data);
        setFeesInput({
          bank_fee_rate: String(Math.round(fs.data.bank_fee_rate * 10000) / 100),
          sales_commission_rate: String(Math.round(fs.data.sales_commission_rate * 10000) / 100),
          default_affiliate_rate: String(Math.round(fs.data.default_affiliate_rate * 10000) / 100),
        });
      }
    })();
  }, []);

  const saveFees = async () => {
    const parsed = {
      bank_fee_rate: parseFloat(feesInput.bank_fee_rate) / 100,
      sales_commission_rate: parseFloat(feesInput.sales_commission_rate) / 100,
      default_affiliate_rate: parseFloat(feesInput.default_affiliate_rate) / 100,
    };
    if (Object.values(parsed).some((v) => isNaN(v) || v < 0 || v > 0.5)) {
      return toast.error("Taxas inválidas — use valores entre 0 e 50 (%)");
    }
    setSavingFees(true);
    try {
      const { data: r } = await api.put("/admin/settings/fees", parsed);
      setFees(r);
      toast.success("Taxas atualizadas — aplicadas a partir de agora");
    } catch (e) { toast.error(errMsg(e, "Erro ao salvar taxas")); }
    finally { setSavingFees(false); }
  };

  const toggleAllowDelete = async () => {
    const next = !allowDelete;
    setAllowDelete(next);
    try {
      await api.post("/admin/settings/delete-tx", { enabled: next });
      toast.success(next ? "Clientes agora podem excluir transações" : "Exclusão de transações desabilitada para clientes");
    } catch { toast.error("Erro ao salvar"); setAllowDelete(!next); }
  };

  const changeWMode = async (m) => {
    setWMode(m);
    try {
      await api.post("/admin/settings/withdraw-mode", { mode: m });
      toast.success(`Modo de saque: ${m === "auto" ? "Automático" : "Manual"}`);
    } catch { toast.error("Erro ao salvar modo"); }
  };

  const saveMP = async () => {
    if (!settings.access_token) return toast.error("Cole seu Access Token do Mercado Pago");
    setSaving(true);
    try {
      await api.post("/admin/settings", settings);
      toast.success("Credenciais Mercado Pago salvas");
      const { data: s } = await api.get("/admin/settings");
      setPreview(s.access_token_preview);
      setSettings({ ...settings, access_token: "" });
    } catch (e) { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="text-white/60">Carregando painel...</div>;

  const stats = [
    { l: "Comissão total ganha", v: brl(data.commission_earned), i: Coins, c: "var(--accent)", tid: "admin-commission-earned" },
    { l: "Volume bruto processado", v: brl(data.gross_volume), i: ChartLineUp, c: "var(--success)", tid: "admin-volume" },
    { l: "Saldo da plataforma", v: brl(data.commission_balance), i: Money, c: "var(--accent-secondary)", tid: "admin-balance" },
    { l: "Clientes ativos", v: data.users, i: Users, c: "#fff", tid: "admin-users" },
  ];

  const inp = "w-full bg-[var(--bg-base)] border border-[var(--border)] rounded-sm px-4 py-3 focus:border-[var(--accent)] outline-none font-mono text-sm";

  return (
    <div className="fadeUp">
      <div className="label-caps mb-2">Painel plataforma</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-10">Comissões & Configurações</h1>

      <div className="card-flat p-6 mb-8" data-testid="delete-tx-toggle-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-caps mb-2">Permissões dos clientes</div>
            <div className="font-display font-bold text-xl">Permitir que clientes excluam transações do próprio extrato</div>
            <p className="text-white/60 text-sm mt-2">Quando desligado, o botão de excluir some do dashboard do cliente. Você (admin) sempre pode excluir.</p>
          </div>
          <button
            onClick={toggleAllowDelete}
            data-testid="toggle-allow-delete"
            className={`relative inline-flex h-8 w-16 items-center rounded-full transition ${allowDelete ? "bg-[var(--accent)]" : "bg-[var(--border)]"}`}
          >
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition ${allowDelete ? "translate-x-9" : "translate-x-1"}`} />
          </button>
        </div>
      </div>

      <div className="card-flat p-6 mb-8">
        <div className="label-caps mb-2">Modo de processamento de saques</div>
        <div className="font-display font-bold text-xl mb-3">Automático ou Manual</div>
        <p className="text-white/60 text-sm mb-4">
          <b>Automático</b>: sistema tenta enviar o Pix via API MP no ato. <b>Manual</b>: você envia o Pix pelo app do MP e depois clica em &quot;Enviado&quot; no painel de saques (recomendado enquanto o payout automático do MP não confirma direto).
        </p>
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => changeWMode("auto")} data-testid="mode-auto" className={`p-4 border rounded-sm text-left transition ${wMode === "auto" ? "border-[var(--accent)] bg-[var(--accent)]/10" : "border-[var(--border)] hover:border-white/40"}`}>
            <div className="font-display font-bold">Automático</div>
            <div className="text-xs text-white/60 mt-1">Sistema tenta enviar Pix via MP na hora.</div>
          </button>
          <button onClick={() => changeWMode("manual")} data-testid="mode-manual" className={`p-4 border rounded-sm text-left transition ${wMode === "manual" ? "border-[var(--accent)] bg-[var(--accent)]/10" : "border-[var(--border)] hover:border-white/40"}`}>
            <div className="font-display font-bold">Manual</div>
            <div className="text-xs text-white/60 mt-1">Você envia pelo app MP e confirma no painel.</div>
          </button>
        </div>
      </div>

      <div className="card-flat p-6 mb-8" data-testid="fees-card">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="label-caps flex items-center gap-2 mb-2"><Percent size={14} /> Taxas da plataforma</div>
            <div className="font-display font-bold text-xl">Quanto você cobra em cada operação</div>
          </div>
          <div className="text-xs text-white/40">Atualização entra em vigor em novas transações</div>
        </div>
        <p className="text-white/60 text-sm mb-4">
          Valores em <b>%</b> (percentual). Vale para todas as movimentações a partir do momento em que você salvar.
          Transações antigas mantêm a taxa que estava vigente no momento em que ocorreram.
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="label-caps block mb-2">Taxa bancária (Pix/Depósito/Saque/Transf.)</label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                min="0"
                max="50"
                value={feesInput.bank_fee_rate}
                onChange={(e) => setFeesInput({ ...feesInput, bank_fee_rate: e.target.value })}
                data-testid="fee-bank-input"
                className={inp + " pr-10"}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 font-mono text-sm">%</span>
            </div>
            <div className="mt-1 text-xs text-white/40">Atual: {(fees.bank_fee_rate * 100).toFixed(2)}%</div>
          </div>
          <div>
            <label className="label-caps block mb-2">Comissão em vendas de funil</label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                min="0"
                max="50"
                value={feesInput.sales_commission_rate}
                onChange={(e) => setFeesInput({ ...feesInput, sales_commission_rate: e.target.value })}
                data-testid="fee-sales-input"
                className={inp + " pr-10"}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 font-mono text-sm">%</span>
            </div>
            <div className="mt-1 text-xs text-white/40">Atual: {(fees.sales_commission_rate * 100).toFixed(2)}%</div>
          </div>
          <div>
            <label className="label-caps block mb-2">Comissão padrão do afiliado</label>
            <div className="relative">
              <input
                type="number"
                step="0.1"
                min="0"
                max="50"
                value={feesInput.default_affiliate_rate}
                onChange={(e) => setFeesInput({ ...feesInput, default_affiliate_rate: e.target.value })}
                data-testid="fee-affiliate-input"
                className={inp + " pr-10"}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 font-mono text-sm">%</span>
            </div>
            <div className="mt-1 text-xs text-white/40">Atual: {(fees.default_affiliate_rate * 100).toFixed(2)}%</div>
          </div>
        </div>
        <button
          onClick={saveFees}
          disabled={savingFees}
          data-testid="save-fees"
          className="btn-primary mt-4 flex items-center gap-2"
        >
          <FloppyDisk size={16} weight="bold" /> {savingFees ? "Salvando..." : "Aplicar novas taxas"}
        </button>
      </div>

      <div className="card-flat p-6 mb-8">
        <div className="label-caps flex items-center gap-2 mb-2"><Key size={14} /> Mercado Pago da plataforma</div>
        <div className="font-display font-bold text-xl mb-1">Suas credenciais (tudo passa pela sua conta)</div>
        <p className="text-white/60 text-sm mb-4">Cole seu <b>Access Token</b> do Mercado Pago. Todas as movimentações financeiras (Pix, transferências, saques, vendas) serão processadas na sua conta MP e recebidas no seu CNPJ. Cada transação registra o CPF/CNPJ do cliente.</p>
        {preview && <div className="text-xs text-white/50 mb-3">Token atual: <code className="text-[var(--accent)]" data-testid="mp-token-preview">{preview}</code></div>}
        <div className="grid md:grid-cols-2 gap-3">
          <input placeholder="APP_USR-xxxxxxxx (Access Token)" value={settings.access_token} onChange={(e) => setSettings({ ...settings, access_token: e.target.value })} data-testid="mp-access-token" className={inp} />
          <input placeholder="APP_USR-xxxxxxxx (Public Key)" value={settings.public_key} onChange={(e) => setSettings({ ...settings, public_key: e.target.value })} data-testid="mp-public-key" className={inp} />
          <select value={settings.mode} onChange={(e) => setSettings({ ...settings, mode: e.target.value })} data-testid="mp-mode" className={inp}>
            <option value="live">Produção (live)</option>
            <option value="mock">Sandbox / Mock</option>
          </select>
          <button onClick={saveMP} disabled={saving} className="btn-primary flex items-center gap-2 justify-center" data-testid="save-mp">
            <FloppyDisk size={16} weight="bold" /> {saving ? "Salvando..." : "Salvar credenciais"}
          </button>
        </div>
        <div className="mt-4 text-xs text-white/50 grid md:grid-cols-2 gap-2">
          <div>Comissão em vendas de link: <b className="text-[var(--accent)]">{(fees.sales_commission_rate * 100).toFixed(2)}%</b></div>
          <div>Taxa em movimentações bancárias: <b className="text-[var(--accent)]">{(fees.bank_fee_rate * 100).toFixed(2)}%</b></div>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-8">
        {stats.map((s, i) => (
          <div key={i} className="card-flat p-6" data-testid={s.tid}>
            <s.i size={24} color={s.c} weight="duotone" />
            <div className="label-caps mt-4">{s.l}</div>
            <div className="font-display font-black text-2xl mt-2" style={{ color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="card-flat p-6 mb-8">
        <div className="label-caps mb-2">Comissões diárias (últimos 14 dias)</div>
        <div className="font-display font-bold text-xl mb-4 flex items-center gap-2"><ChartBar size={20} /> Faturamento por dia</div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={data.chart}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a2d35" />
            <XAxis dataKey="_id" stroke="rgba(255,255,255,0.5)" fontSize={11} />
            <YAxis stroke="rgba(255,255,255,0.5)" fontSize={11} />
            <Tooltip contentStyle={{ background: "#121418", border: "1px solid #2a2d35", borderRadius: 2 }} />
            <Line type="monotone" dataKey="fees" stroke="#FFB30F" strokeWidth={2} dot={{ fill: "#FFB30F" }} name="Comissão" />
            <Line type="monotone" dataKey="volume" stroke="#10B981" strokeWidth={1} dot={false} name="Volume" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="card-flat p-6"><div className="label-caps">Transações totais</div><div className="font-display font-bold text-3xl mt-2">{data.transactions}</div></div>
        <div className="card-flat p-6"><div className="label-caps">Funis publicados</div><div className="font-display font-bold text-3xl mt-2">{data.funnels}</div></div>
        <div className="card-flat p-6"><div className="label-caps">Leads capturados</div><div className="font-display font-bold text-3xl mt-2">{data.leads}</div></div>
      </div>

      {taxReport && (
        <div className="card-flat p-6 mt-8" data-testid="tax-report">
          <div className="label-caps flex items-center gap-2 mb-2"><Receipt size={14} /> Relatório fiscal (Imposto de Renda)</div>
          <div className="font-display font-bold text-xl mb-4">Separação: renda vs dinheiro em trânsito</div>
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="border border-[var(--accent)] bg-[var(--accent)]/5 p-4 rounded-sm">
              <div className="label-caps text-[var(--accent)]">Sua RENDA tributável (taxas de serviço)</div>
              <div className="font-display font-black text-3xl text-[var(--accent)] mt-2" data-testid="tax-taxable">{brl(taxReport.platform_revenue_taxable)}</div>
              <div className="text-xs text-white/60 mt-2">É este valor que você declara à Receita Federal como faturamento da plataforma.</div>
            </div>
            <div className="border border-white/20 p-4 rounded-sm">
              <div className="label-caps">Volume em trânsito (não é sua renda)</div>
              <div className="font-display font-black text-3xl mt-2" data-testid="tax-passthrough">{brl(taxReport.total_volume_passthrough)}</div>
              <div className="text-xs text-white/60 mt-2">Dinheiro que passou pela sua conta MP mas pertence aos clientes. NÃO é receita sua — cada cliente responde pelo seu.</div>
            </div>
          </div>
          <div className="text-xs text-white/50 mb-3">Comissão por tipo de operação:</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            {["deposit","transfer","withdraw","funnel_sale","video_sale"].map((k) => {
              const b = taxReport.by_type[k] || {};
              return (
                <div key={k} className="border border-[var(--border)] rounded-sm p-3">
                  <div className="label-caps">{k}</div>
                  <div className="font-mono text-white/80 mt-1">{b.count || 0} ops</div>
                  <div className="text-[var(--accent)]">Taxa: {brl(b.fees || 0)}</div>
                  <div className="text-white/50">Vol: {brl(b.gross || 0)}</div>
                </div>
              );
            })}
          </div>
          {taxReport.top_contributors?.length > 0 && (
            <div className="mt-6">
              <div className="label-caps mb-2">Top contribuintes (por CPF/CNPJ)</div>
              <div className="text-xs divide-y divide-[var(--border)]">
                {taxReport.top_contributors.slice(0, 10).map((r, i) => (
                  <div key={r._id || `row-${i}`} className="py-2 grid grid-cols-4 gap-2">
                    <div className="col-span-2">
                      <div className="text-white/80">{r.user?.name || "—"}</div>
                      <div className="text-white/40">{r.user?.email}</div>
                    </div>
                    <div className="font-mono text-white/50">{r.user?.cpf_cnpj || "-"}</div>
                    <div className="text-right">
                      <div className="text-[var(--accent)]">{brl(r.fees)}</div>
                      <div className="text-white/40">{r.count} ops</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* PLANOS DE ASSINATURA */}
      <div className="mt-8">
        <AdminPlansSection />
      </div>

      {/* CHAT COM CLIENTES */}
      <div className="mt-8">
        <AdminChatSection />
      </div>
    </div>
  );
}
